import { Component, inject, OnInit  } from "@angular/core";
import { ButtonModule               } from 'primeng/button';
import { TableModule                } from 'primeng/table'
import { lastValueFrom              } from "rxjs";

import { GlobalRedirectService      } from "app/@routes/redirect.service";
import { CustomerApi                } from "nv@api/customer.api.service";
import { ReportApi                  } from "nv@api/report.api.service";
import { CcTable                    } from "nv@components/cc-table/cc-table.component";
import { ApiResponseModel           } from "nv@services/http.service";
import { LoaderService              } from "nv@services/loader.service";
import { TranslatePipe              } from "nv@services/translate.service";

import { DashboardState             } from "./dashboard.state";
import { SidebarManageCustomers     } from "../../@modals/manage-customers/manage-customers.component";
import { SidebarGenerateExcelReport } from "../../@modals/generate-excel-report/generate-excel-report.component";

@Component({
  selector    : 'page-main-customer',
  templateUrl : './dashboard.component.html',
  providers   : [DashboardState],
  imports     : [
    ButtonModule                ,
    TableModule                 ,
    CcTable                     ,
    SidebarManageCustomers      ,
    SidebarGenerateExcelReport  ,
    TranslatePipe
  ]
})
export class DashboardPage implements OnInit {

  public $$ui             = inject(DashboardState)
  private globalRedirect  = inject(GlobalRedirectService);
  private loaderService   = inject(LoaderService);
  private $customerApi    = inject(CustomerApi)
  private $reportApi      = inject(ReportApi)

  public customerCollection: any = [];

  public async ngOnInit() {
    await this.fetchCustomer();
  }

  /**
   * @author Mihail Petrov
   */
  public processOnCreateNewCustomer() {

    this.$$ui.state.isSidebarCreateNewCustomerVisible = true
    this.$$ui.data.sidebarTransactionAction           = 'create';
  }

  /**
   * @author Mihail Petrov
   */
  public processOnImportNewCustomer() {

    this.$$ui.state.isSidebarCreateNewCustomerVisible = true
    this.$$ui.data.sidebarTransactionAction           = 'import';
  }

  /**
   * @author Mihail Petrov
   * @param $event
   */
  public processOnCustomerEdit($event: any) {

    this.$$ui.state.isSidebarCreateNewCustomerVisible = true;
    this.$$ui.data.sidebarTransactionObject           = $event;
    this.$$ui.data.sidebarTransactionAction           = 'edit';
  }

  /**
   * @author Mihail Petrov
   * @param $event
   */
  public processOnCustomerDelete($event: any) {

    this.$$ui.state.isSidebarCreateNewCustomerVisible = true;
    this.$$ui.data.sidebarTransactionObject           = $event;
    this.$$ui.data.sidebarTransactionAction           = 'delete';
  }

  /**
   *
   * @param $event
   */
  public processOnCustomerNavigate($event: any) {
    this.globalRedirect.inContextOfCustomer().viewSpecificCustomer($event.id);
  }

  /**
   * Opens the sidebar for generating a report from an Excel file
   */
  public processOnGenerateReportFromExcel() {

    this.$$ui.state.isSidebarGenerateExcelReportVisible = true;
  }

  /**
   * Handles the uploaded Excel file and sends it to the backend
   */
  public async processGenerateReport($formData: any) {

    try {
      const response = await lastValueFrom(this.$reportApi.generateReportFromExcel($formData));

      // Check if response body is a JSON blob (error response)
      if (response.body instanceof Blob && response.body.type === 'application/json') {
        const errorText = await response.body.text();
        const errorObj  = JSON.parse(errorText);
        this.loaderService.showErrorModal(errorObj);
        return;
      }

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'SiteReport.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.body], { type: 'application/pdf' });
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href     = url;
      link.download = filename;
      link.click();

      window.URL.revokeObjectURL(url);
    }
    catch(error: any) {
      // Handle HTTP error - error.error may be a Blob
      if (error.error instanceof Blob) {
        const errorText = await error.error.text();
        try {
          const errorObj = JSON.parse(errorText);
          this.loaderService.showErrorModal(errorObj);
        } catch {
          this.loaderService.showErrorModal({ errorMessage: errorText });
        }
      } else {
        this.loaderService.showErrorModal(error.error || { errorMessage: 'Report generation failed' });
      }
    }
  }

  /**
   * @author Mihail Petrov
   * @param $obj
   */
  public async saveCustomer($obj: any) {

    if(this.$$ui.data.sidebarTransactionAction == "create"  ) await this.createNewCustomer($obj);
    if(this.$$ui.data.sidebarTransactionAction == "edit"    ) await this.editExistingCustomer($obj);
    if(this.$$ui.data.sidebarTransactionAction == "delete"  ) await this.removeExistingCustomer($obj);
    if(this.$$ui.data.sidebarTransactionAction == "import"  ) await this.importCustomer($obj);
  }

  /**
   * @author Mihail Petrov
   * @param $obj
   */
  public async importCustomer($obj: any) {

    const response = await lastValueFrom(this.$customerApi.importCustomer($obj));
    await this.fetchCustomer();
  }

  /**
   * @author Mihail Petrov
   * @param $pageId
   */
  public async processOnChangePage($pageId: any) {

    const response          = await lastValueFrom(this.$customerApi.getAllCustomers($pageId));
    this.customerCollection = response.data;
  }

  /**
   * @author Mihail Petrov
   * @param $searchValue
   */
  public async processOnSearchCustomer($searchValue: string) {

    const response          = await lastValueFrom(this.$customerApi.getAllCustomers({ name: $searchValue}));
    this.customerCollection = response.data;
  }


  /**
   * @author Mihail Petrov
   * @param $obj
   */
  private async createNewCustomer($obj: any) {

    try {

      const response: ApiResponseModel  = await lastValueFrom(this.$customerApi.createNewCustomer($obj));
      const pkid: any                   = response.data.id;

      this.globalRedirect.inContextOfCustomer().viewSpecificCustomer(pkid);
    }
    catch(error) {
      console.log(error)
    }
  }

  /**
   * @author Mihail Petrov
   * @param $obj
   */
  private async editExistingCustomer($obj: any) {

    await lastValueFrom(this.$customerApi.updateCustomer($obj));
    this.customerCollection = (await lastValueFrom(this.$customerApi.getAllCustomers())).data;
  }

  /**
   * @author Mihail Petrov
   */
  private async removeExistingCustomer($obj: any) {

    await lastValueFrom(this.$customerApi.removeCustomer($obj.id));
    this.customerCollection = (await lastValueFrom(this.$customerApi.getAllCustomers())).data;
  }

  /**
   *
   */
  private async fetchCustomer() {

    const response              = await lastValueFrom(this.$customerApi.getAllCustomers());
    this.customerCollection     = response.data;
    this.$$ui.state.hasCustomer = this.customerCollection.length > 0;
  }
}
